import { EmployeeViewCard } from './EmployeeViewCard';
import { ManagerViewCard } from './ManagerViewCard';
import { DEFAULT_MANAGER_PIVOT, isManagerView } from '../utils/calibrationViews';

import type { JobTitleLabels } from '../utils/jobTitleLabels';
import type { CalibrationReport, CalibrationView } from '@/types/calibration';

import { EmptyState } from '@/components/EmptyState/EmptyState';
import { Bone, Skeleton } from '@/components/Skeleton/Skeleton';
import { useAmelioTranslations } from '@/hooks';

export interface CalibrationReportContentProps {
    report: CalibrationReport | undefined;
    isLoading: boolean;
    isError: boolean;
    activeView: CalibrationView;
    onViewChange: (view: CalibrationView) => void;
    /** Gates the employee card the same way the view tiles gate its tile, so a team manager can
     * never land on it through the URL either. */
    canManageAdjustments: boolean;
    language: string;
    jobTitleLabels: JobTitleLabels;
    onBulkAdjust: () => void;
    onPublish: () => void;
}

/**
 * The report itself, under the header, the view tiles and the filter bar: whichever card the
 * active view calls for — or the one state that replaces it, written as early returns so a reader
 * can see at a glance which case wins.
 */
export const CalibrationReportContent = ({
    report,
    isLoading,
    isError,
    activeView,
    onViewChange,
    canManageAdjustments,
    language,
    jobTitleLabels,
    onBulkAdjust,
    onPublish,
}: CalibrationReportContentProps) => {
    const { t } = useAmelioTranslations();

    if (isLoading) {
        return (
            <Skeleton className={'flex flex-col gap-4'}>
                <Bone className={'h-20 w-full'} />

                <Bone className={'h-80 w-full'} />
            </Skeleton>
        );
    }

    if (isError || !report) {
        return (
            <EmptyState
                title={t('calibration.errors.loadReport.title')}
                message={t('calibration.errors.loadReport.message')}
            />
        );
    }

    // An empty report is the filters' doing far more often than the cycle's, so the filter bar
    // above stays on screen: the way out of this state is to widen the selection.
    if (report.managers.length === 0) {
        return (
            <EmptyState
                title={t('calibration.summary.empty.title')}
                message={t('calibration.summary.empty.message')}
            />
        );
    }

    if (activeView === 'employees' && canManageAdjustments) {
        return (
            <EmployeeViewCard
                report={report}
                language={language}
                jobTitleLabels={jobTitleLabels}
                onBulkAdjust={onBulkAdjust}
                onPublish={onPublish}
            />
        );
    }

    // A caller who cannot manage adjustments falls back to a manager pivot rather than to a blank
    // card: the page already rewrites such a URL, and this keeps the promise even if it does not.
    return (
        <ManagerViewCard
            report={report}
            activeView={isManagerView(activeView) ? activeView : DEFAULT_MANAGER_PIVOT}
            onViewChange={onViewChange}
            language={language}
        />
    );
};
